export const PH_BOOTSTRAP_COOKIE = 'ph_bootstrap';
export const HOMEPAGE_V2_FLAG = 'homepage-v2-experiment';
export const PRIVATE_PAGE_V2_FLAG = 'private-page-v2-experiment';

export type HomepageVariant = 'control' | 'v2';
export type SearchParamValue = string | string[] | null | undefined;

export const firstSearchParamValue = (value: SearchParamValue): string | undefined => {
  if (Array.isArray(value)) return value[0];
  return value ?? undefined;
};

export const isSe3EnabledParam = (value: SearchParamValue): boolean => {
  const param = firstSearchParamValue(value);
  return param === '' || param === '1' || param === 'true';
};

export const isSe3DisabledParam = (value: SearchParamValue): boolean => {
  const param = firstSearchParamValue(value);
  return param === '0' || param === 'false';
};

export type SearchParamsRecord = Record<string, SearchParamValue> | undefined | null;

/**
 * True when a query param forces an experiment/QA variant of a canonical page
 * (SE3 editorial preview, private A/B override, Builder layout preview). Such
 * URLs render a non-default variant and must never be indexed as duplicates of
 * the real page, so `generateMetadata` emits noindex when this returns true.
 */
export const hasExperimentPreviewParam = (searchParams: SearchParamsRecord): boolean => {
  if (!searchParams) return false;
  if (isSe3EnabledParam(searchParams.se3)) return true;
  if (firstSearchParamValue(searchParams.v3) === 'true') return true;
  if (firstSearchParamValue(searchParams.v2) === 'true') return true;
  if (firstSearchParamValue(searchParams.layout) === 'builder') return true;
  if (firstSearchParamValue(searchParams.se2_variant) !== undefined) return true;
  if (firstSearchParamValue(searchParams.private_variant) !== undefined) return true;
  return false;
};

export const NOINDEX_ROBOTS = { index: false, follow: false } as const;

export interface BootstrapData {
  distinctId?: string;
  featureFlags: Record<string, string | boolean>;
  timestamp?: number;
  evaluatedPath?: string;
  isNewVisitor?: boolean;
  experienceScore?: number;
  predictedExperience?: 'accredited' | 'retail';
  predictedExperienceSource?:
    | 'model'
    | 'model_cached'
    | 'heuristic'
    | 'verified'
    | 'verified_cached';
  predictedExperienceAt?: number;
  verifiedExperience?: 'accredited' | 'retail';
  verifiedExperienceAt?: number;
  scoringSignals?: string[];
  originalReferer?: string;
  scoreChangeEmittedAt?: number;
}

export const parseBootstrapCookie = (cookieValue: string | undefined): BootstrapData | null => {
  if (!cookieValue) return null;
  try {
    return JSON.parse(cookieValue) as BootstrapData;
  } catch {
    return null;
  }
};

export const getHomepageVariantFromBootstrap = (
  bootstrap: BootstrapData | null,
): HomepageVariant | null => {
  if (!bootstrap) return null;
  const flagValue = bootstrap.featureFlags?.[HOMEPAGE_V2_FLAG];
  if (flagValue === undefined) return null;
  if (flagValue === 'v2' || flagValue === true) return 'v2';
  return 'control';
};

export const getServerHomepageVariant = (cookieValue: string | undefined): HomepageVariant => {
  const bootstrap = parseBootstrapCookie(cookieValue);
  return getHomepageVariantFromBootstrap(bootstrap) || 'control';
};

export const getPrivatePageVariantFromBootstrap = (
  bootstrap: BootstrapData | null,
): HomepageVariant | null => {
  if (!bootstrap) return null;
  const flagValue = bootstrap.featureFlags?.[PRIVATE_PAGE_V2_FLAG];
  if (flagValue === undefined) return null;
  if (flagValue === 'v2' || flagValue === true) return 'v2';
  return 'control';
};

export const getServerPrivatePageVariant = (cookieValue: string | undefined): HomepageVariant => {
  const bootstrap = parseBootstrapCookie(cookieValue);
  return getPrivatePageVariantFromBootstrap(bootstrap) || 'control';
};

export type VisitorType = 'new' | 'returning';

export type GlobalVisitorEventProperties = {
  investor_signals_is_new_visitor: boolean;
  visitor_type: VisitorType;
};

/**
 * Converts the proxy-derived visitor signal into PostHog super properties.
 * Returning null is intentional: when the bootstrap cookie is unavailable we
 * must not silently classify the visitor as returning.
 */
export const getGlobalVisitorEventProperties = (
  bootstrap: BootstrapData | null,
): GlobalVisitorEventProperties | null => {
  if (typeof bootstrap?.isNewVisitor !== 'boolean') return null;

  return {
    investor_signals_is_new_visitor: bootstrap.isNewVisitor,
    visitor_type: bootstrap.isNewVisitor ? 'new' : 'returning',
  };
};

export const getVisitorTypeFromBootstrap = (bootstrap: BootstrapData | null): VisitorType => {
  if (!bootstrap) return 'returning';
  return bootstrap.isNewVisitor ? 'new' : 'returning';
};

export const getBootstrapFromDocumentCookie = (): BootstrapData | null => {
  if (typeof document === 'undefined') return null;
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${PH_BOOTSTRAP_COOKIE}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      if (cookieValue) {
        return JSON.parse(decodeURIComponent(cookieValue)) as BootstrapData;
      }
    }
  } catch {
    // Malformed cookie
  }
  return null;
};
